import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, SearchIcon, TrashIcon } from './Icons';
import Pagination from './Pagination';
import SelectMenu from './SelectMenu';
import DateRangePicker from './DateRangePicker';
import InternationalOrders from './InternationalOrders';
import IntlOrderDetail from './IntlOrderDetail';
import { CreateIntlOrderModal, hasCreateDraft } from './IntlOrderForm';
import {
  ADDRESS_META, PAYMENT_META, QUEUES, STATUS_META, StatusPill, confirmOrderDelete, defaultQueueFor, formatDateTime, formatMoney, nextStep,
  queueCount, readJson, staleUnpaidDays, userCaps, customerShippingPaise,
} from './intlOrderUtils';

const DEFAULT_PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 15000;
const SEARCH_DEBOUNCE_MS = 300;
// Mirrors the server: an order the customer has paid for can't be deleted.
const PAID_STATUSES = ['paid', 'partially_paid'];

const PAYMENT_OPTIONS = [{ value: '', label: 'All payments' }, ...Object.entries(PAYMENT_META).map(([value, m]) => ({ value, label: m.label }))];
const ADDRESS_OPTIONS = [{ value: '', label: 'All address states' }, ...Object.entries(ADDRESS_META).map(([value, m]) => ({ value, label: m.label }))];

// International Orders page: the workflow list (search, filters and
// pagination all run on the server) plus the older manual log on its own
// tab. `prefillNumber` comes from the WhatsApp page's "International order"
// button and opens the create form for that customer.
function IntlOrders({ prefillNumber, onPrefillConsumed }) {
  const { authFetch, user } = useAuth();
  const caps = userCaps(user);
  const [view, setView] = useState('orders'); // 'orders' | 'legacy'
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ byStatus: {} });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // A QUEUES key ('' = all). Logistics-only and packing-only users start on
  // their own queue.
  const [status, setStatus] = useState(() => defaultQueueFor(caps));
  const [paymentStatus, setPaymentStatus] = useState('');
  const [addressStatus, setAddressStatus] = useState('');
  const [country, setCountry] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(() => caps.create && hasCreateDraft());
  const [createPrefill, setCreatePrefill] = useState(null);
  const [notice, setNotice] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!prefillNumber) return;
    if (caps.create) {
      setView('orders');
      setCreatePrefill(prefillNumber);
      setCreating(true);
    }
    onPrefillConsumed?.();
  }, [prefillNumber, caps.create, onPrefillConsumed]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, status, paymentStatus, addressStatus, country, fromDate, toDate, pageSize]);

  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set('q', search);
    const queue = QUEUES.find((q) => q.key === status);
    if (queue) params.set('status', queue.statuses.join(','));
    if (paymentStatus) params.set('payment_status', paymentStatus);
    if (addressStatus) params.set('address_status', addressStatus);
    if (country) params.set('country', country);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    try {
      const [list, counts] = await Promise.all([
        readJson(await authFetch(`/api/international-orders?${params}`), 'Could not load international orders.'),
        readJson(await authFetch('/api/international-orders/summary'), 'Could not load order counts.'),
      ]);
      setOrders(list.orders || []);
      setTotal(list.total || 0);
      setSummary(counts);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authFetch, page, pageSize, search, status, paymentStatus, addressStatus, country, fromDate, toDate]);

  useEffect(() => {
    if (view !== 'orders') return undefined;
    fetchOrders();
    const interval = setInterval(() => fetchOrders({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchOrders, view]);

  useEffect(() => {
    if (view !== 'orders') return;
    authFetch('/api/international-orders/countries')
      .then((response) => readJson(response, ''))
      .then((body) => setCountries(body.countries || []))
      .catch(() => {});
  }, [authFetch, view, summary]);

  const totalAll = Object.values(summary.byStatus || {}).reduce((sum, n) => sum + n, 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const filtersActive = search || status || paymentStatus || addressStatus || country || fromDate || toDate;

  const handleCreated = (order, submitted) => {
    setCreating(false);
    setCreatePrefill(null);
    setNotice(
      submitted
        ? `${order.order_number} created. Waiting for logistics to verify the address and add shipping charges.`
        : `${order.order_number} saved as a draft. Send it to logistics when the details are complete.`
    );
    fetchOrders({ silent: true });
    setOpenId(order.id);
  };

  // Admin-only. Also called from the order page (onDeleted below).
  const handleDelete = async (order) => {
    const body = confirmOrderDelete(order);
    if (!body) return;
    setDeletingId(order.id);
    setError('');
    try {
      await readJson(await authFetch(`/api/international-orders/${order.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }), 'Could not delete this order.');
      setNotice(`${order.order_number} deleted.`);
      fetchOrders({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setStatus('');
    setPaymentStatus('');
    setAddressStatus('');
    setCountry('');
    setFromDate('');
    setToDate('');
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <div className="customer-toggle" role="group" aria-label="International orders view">
          <button type="button" className={view === 'orders' ? 'active' : ''} onClick={() => setView('orders')}>Orders</button>
          <button type="button" className={view === 'legacy' ? 'active' : ''} onClick={() => setView('legacy')}>Legacy log</button>
        </div>
        {view === 'orders' && caps.create && (
          <button type="button" className="btn btn-primary intl-new-btn" onClick={() => { setCreatePrefill(null); setCreating(true); }}>
            New order
          </button>
        )}
      </div>

      {view === 'legacy' ? (
        <>
          <p className="intl-muted intl-legacy-note">
            Orders logged before the workflow existed. Kept as they were, and can still be edited here.
          </p>
          <InternationalOrders />
        </>
      ) : (
        <>
          <div className="stat-cards intl-stats">
            <button type="button" className={`stat-card ${status === '' ? 'active' : ''}`} onClick={() => setStatus('')}>
              <div className="stat-value">{totalAll}</div>
              <div className="stat-label">All orders</div>
            </button>
            {QUEUES.map((queue) => {
              const count = queueCount(queue, summary);
              const toAccept = summary.byStatus?.LOGISTICS_PENDING || 0;
              return (
                <button
                  key={queue.key}
                  type="button"
                  className={`stat-card intl-stat-${queue.tone} ${status === queue.key ? 'active' : ''}`}
                  onClick={() => setStatus(status === queue.key ? '' : queue.key)}
                >
                  <div className="stat-value">{count}</div>
                  <div className="stat-label">
                    {queue.label}
                    {queue.key === 'logistics' && toAccept > 0 && <span className="intl-stat-flag"> · {toAccept} to accept</span>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="incoming-filters-bar">
            <button type="button" className="btn btn-secondary" onClick={() => fetchOrders()} disabled={loading}>
              <RefreshIcon width={14} height={14} />
              Refresh
            </button>
            <div className="search-input-wrap">
              <SearchIcon className="search-input-icon" width={15} height={15} />
              <input
                type="text"
                className="search-input"
                placeholder="Order ID, name, WhatsApp, email or AWB"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                aria-label="Search international orders"
              />
            </div>
            <SelectMenu
              value={country}
              onChange={setCountry}
              ariaLabel="Country"
              options={[{ value: '', label: 'All countries' }, ...countries.map((c) => ({ value: c, label: c }))]}
            />
            <SelectMenu value={paymentStatus} onChange={setPaymentStatus} ariaLabel="Payment status" options={PAYMENT_OPTIONS} />
            <SelectMenu value={addressStatus} onChange={setAddressStatus} ariaLabel="Address verification" options={ADDRESS_OPTIONS} />
            <DateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              onApply={(from, to) => {
                setFromDate(from);
                setToDate(to);
              }}
            />
            {filtersActive && (
              <button type="button" className="link-btn" onClick={clearFilters}>Clear filters</button>
            )}
          </div>

          {notice && <div className="call-feedback call-feedback-ok intl-feedback">{notice}</div>}
          {error && <div className="call-feedback call-feedback-error">{error}</div>}

          {loading ? (
            <div className="loading-state">Loading…</div>
          ) : (
            <div className="table-container">
              <table className="data-table intl-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Country</th>
                    <th>Products</th>
                    <th className="intl-num">Product total</th>
                    <th className="intl-num">Shipping</th>
                    <th className="intl-num">Final total</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Tracking</th>
                    {caps.admin && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan={caps.admin ? 11 : 10} style={{ textAlign: 'center' }}>
                        {error ? 'Could not load international orders.' : filtersActive ? 'No orders match these filters.' : 'No international orders yet.'}
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr
                        key={order.id}
                        className={`intl-row${staleUnpaidDays(order) || order.courier_recheck_requested_at ? ' intl-row-stale' : ''}`}
                        onClick={() => setOpenId(order.id)}
                        tabIndex={0}
                        onKeyDown={(event) => { if (event.key === 'Enter') setOpenId(order.id); }}
                      >
                        <td>
                          <div className="table-cell-strong">{order.order_number}</div>
                          <div className="intl-muted">{formatDateTime(order.created_at)}</div>
                        </td>
                        <td>
                          <div>{order.customer_name}</div>
                          <div className="intl-muted">{order.customer_number}</div>
                        </td>
                        <td>{order.country || '-'}</td>
                        <td className="intl-products-cell" title={order.products_summary || ''}>
                          {order.products_summary || '-'}
                          {order.item_count > 3 && <span className="intl-muted"> +{order.item_count - 3} more</span>}
                        </td>
                        <td className="intl-num">{formatMoney(order.product_subtotal_paise, order.currency)}</td>
                        <td className="intl-num">
                          {order.shipping_charge_paise === null ? <span className="intl-muted">Pending</span> : formatMoney(customerShippingPaise(order.shipping_charge_paise), order.currency)}
                        </td>
                        <td className="intl-num table-cell-strong">
                          {order.final_total_paise === null ? '-' : formatMoney(order.final_total_paise, order.currency)}
                        </td>
                        <td><StatusPill meta={PAYMENT_META[order.payment_status]} /></td>
                        <td>
                          <StatusPill meta={STATUS_META[order.status]} />
                          {nextStep(order).action && (
                            <div className={`intl-row-action intl-row-action-${nextStep(order).tone}`}>{nextStep(order).action}</div>
                          )}
                        </td>
                        <td>
                          {order.awb_number ? (
                            <>
                              <div>{order.awb_number}</div>
                              {order.tracking_url && (
                                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>
                                  Track
                                </a>
                              )}
                            </>
                          ) : '-'}
                        </td>
                        {caps.admin && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger-outline btn-small"
                              disabled={deletingId === order.id || (PAID_STATUSES.includes(order.payment_status) && !caps.superadmin)}
                              title={PAID_STATUSES.includes(order.payment_status)
                                ? (caps.superadmin ? 'Delete paid test order (type the order number to confirm)' : 'Paid orders can only be cancelled, not deleted')
                                : 'Delete order'}
                              aria-label={`Delete ${order.order_number}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(order);
                              }}
                            >
                              <TrashIcon width={14} height={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalItems={total}
          />
        </>
      )}

      {creating && (
        <CreateIntlOrderModal
          prefillNumber={createPrefill}
          onClose={() => { setCreating(false); setCreatePrefill(null); }}
          onCreated={handleCreated}
        />
      )}
      {openId && (
        <IntlOrderDetail
          orderId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => fetchOrders({ silent: true })}
          onDeleted={(order) => {
            setOpenId(null);
            setNotice(`${order.order_number} deleted.`);
            fetchOrders({ silent: true });
          }}
        />
      )}
    </>
  );
}

export default IntlOrders;
